<script lang="ts">
	import Image from '../../components/image.svelte';
	import type { SbImage, SbLandingPageHeroVariant, SbTextColor, TextAlignment } from '../../types';
	import Grid50image50Container from '../../parts/landingPages/grid50image50Container.svelte';
	import HeroTextContent from '../../parts/landingPages/heroTextContent.svelte';
	import PageGrid from '../../components/pageGrid.svelte';
	import { page } from '$app/state';
	import HeroTextContentNyc from './heroTextContentNyc.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';

	interface Props {
		content: ISbStoryData<{
			variant: SbLandingPageHeroVariant;
			headline: string | ISbRichtext;
			show_subline_on_mobile: boolean;
			subline: string | ISbRichtext;
			text_color: SbTextColor;
			image?: SbImage;
			image_desktop?: SbImage;
			asset: ISbStoryData[];
			alignment: TextAlignment;
			button: ISbStoryData['content'][];
			height: '80svh' | '100svh';
			background_color: 'nyc-green-gradient' | 'nyc-dark-green';
		}>['content'];
	}

	let { content }: Props = $props();

	let pdpPage = $derived(page.route.id === '/(marketing)/food-recycler');

	let height = $derived(`calc(${content.height || '100svh'} - var(--banner-height))`);
</script>

<section
	use:editable={content}
	id="hero"
	class={[
		`text-${content.text_color}`,
		content.background_color === 'nyc-green-gradient' &&
			'bg-gradient-to-b from-[#1D4837] to-[#2C634E]',
		content.background_color === 'nyc-dark-green' && 'bg-green-nyc'
	]}
>
	{#if content.variant === 'full-bleed'}
		<div class="relative" style="height: {height}">
			{#if content.asset?.length > 0}
				<Blok content={content.asset[0]} />
			{:else}
				{#if content.image}
					<Image
						wrapperClass="lg:hidden"
						class="h-full w-full object-cover"
						loading="eager"
						priority="high"
						src={content.image.filename}
						alt={content.image.alt ?? ''}
						title={content.image.title ?? ''}
					/>
				{/if}
				{#if content.image_desktop}
					<Image
						wrapperClass="max-lg:hidden"
						class="h-full w-full object-cover"
						loading="eager"
						priority="high"
						src={content.image_desktop.filename}
						alt={content.image_desktop.alt ?? ''}
						title={content.image_desktop.title ?? ''}
					/>
				{/if}
			{/if}
			<div
				class="absolute inset-x-0 bottom-8 lg:bottom-25"
				class:lg:bottom-7={pdpPage}
				class:3xl:bottom-22={pdpPage}
			>
				<PageGrid>
					<HeroTextContentNyc
						class="col-span-full {content.alignment === 'left' ? 'lg:col-end-8' : ''}"
						showSublineOnMobile={content.show_subline_on_mobile}
						headline={content.headline}
						subline={content.subline}
						alignment={content.alignment}
						button={content.button[0]}
					/>
				</PageGrid>
			</div>
		</div>
	{:else}
		<div
			class="flex flex-col gap-y-7 px-4 pb-4 pt-20 lg:hidden"
			class:flex-col-reverse={content.variant === 'image-bottom'}
			style="min-height: {height}"
		>
			{#if content.image}
				<Image
					class="aspect-3/2 h-full w-full rounded-[20px] object-cover"
					loading="eager"
					priority="high"
					src={content.image.filename}
					alt={content.image.alt ?? ''}
					title={content.image.title ?? ''}
				/>
			{/if}

			<HeroTextContentNyc
				headline={content.headline}
				subline={content.subline}
				showSublineOnMobile={content.show_subline_on_mobile}
				alignment={content.alignment}
				extraPadding
				button={content.button[0]}
			/>
		</div>

		<Grid50image50Container
			class="max-lg:hidden"
			alignment={content.variant === 'image-top' ? 'imageRight' : 'imageLeft'}
		>
			{#snippet grid()}
				<div>
					<HeroTextContentNyc
						headline={content.headline}
						subline={content.subline}
						showSublineOnMobile={content.show_subline_on_mobile}
						button={content.button[0]}
					/>
				</div>
			{/snippet}

			{#snippet image()}
				<div class="h-full">
					{#if content.image_desktop}
						<Image
							class="h-full w-full rounded-3xl object-cover"
							loading="eager"
							priority="high"
							src={content.image_desktop.filename}
							alt={content.image_desktop.alt ?? ''}
							title={content.image_desktop.title ?? ''}
						/>
					{/if}
				</div>
			{/snippet}
		</Grid50image50Container>
	{/if}
</section>
